/**
 * Utilitários para a busca de bolões por nome ou por intervalo.
 *
 * Formatos aceitos (o filtro real é aplicado no backend):
 *  - "B7"              -> busca simples
 *  - "B7 - B10"        -> intervalo (B7, B8, B9, B10)
 *  - "B7 a B10", "7-10", "B7..B10"
 *  - "B7-B10, B15"     -> combinação separada por vírgula
 */

export interface ProductRange {
  prefix: string;
  start: number;
  end: number;
}

const RANGE_REGEX =
  /^\s*([a-zA-Z]*)\s*(\d+)\s*(?:-|–|—|\.\.|até|ate|a|to)\s*([a-zA-Z]*)\s*(\d+)\s*$/i;

/** Interpreta um único token como intervalo. Retorna null quando não é intervalo. */
export const parseProductRange = (token: string): ProductRange | null => {
  const match = token.match(RANGE_REGEX);

  if (!match) return null;

  const startPrefix = (match[1] || "").toUpperCase();
  const endPrefix = (match[3] || "").toUpperCase();

  // Prefixos diferentes (ex.: "B7 - C10") não formam um intervalo válido
  if (startPrefix && endPrefix && startPrefix !== endPrefix) return null;

  let start = parseInt(match[2], 10);
  let end = parseInt(match[4], 10);

  if (start > end) [start, end] = [end, start];

  return { prefix: startPrefix || endPrefix, start, end };
};

/** Retorna todos os intervalos identificados no termo de busca. */
export const parseProductRanges = (search: string): ProductRange[] =>
  search
    .split(/[,;]+/)
    .map((token) => token.trim())
    .filter(Boolean)
    .map(parseProductRange)
    .filter((range): range is ProductRange => range !== null);

/** Descrição amigável do intervalo (ex.: "B7 até B10 · 4 bolões"). */
export const describeProductRange = (range: ProductRange): string => {
  const total = range.end - range.start + 1;
  const label = `${range.prefix}${range.start} até ${range.prefix}${range.end}`;

  return `${label} · ${total} ${total === 1 ? "bolão" : "bolões"}`;
};

/**
 * Indica que o usuário ainda está digitando um intervalo
 * (ex.: "B7 -", "B7 a") e a busca deve aguardar.
 */
export const isIncompleteRange = (search: string): boolean => {
  const lastToken = search.split(/[,;]+/).pop()?.trim() ?? "";

  if (!lastToken) return false;

  return /^[a-zA-Z]*\s*\d+\s*(?:-|–|—|\.{1,2}|até|ate|a|to)\s*[a-zA-Z]*\s*$/i.test(
    lastToken
  );
};

/**
 * Verifica se um bolão atende ao termo de busca, para filtros aplicados
 * no cliente (ex.: seleção de produtos em Criar Venda / Fila Pré-venda).
 *
 * Regras por token:
 *  - intervalo ("19-25", "B19 - B25") -> número do bolão dentro da faixa
 *  - apenas números ("19")           -> número começa com o termo (B19, B190)
 *  - com letras ("B19")              -> nome começa com o termo
 */
export const matchesProductSearch = (
  productName: string,
  search: string
): boolean => {
  const tokens = search
    .split(/[,;]+/)
    .map((token) => token.trim())
    .filter(Boolean);

  if (tokens.length === 0) return true;

  const name = (productName || "").trim().toUpperCase();
  const prefix = (name.match(/^[A-Z]+/) || [""])[0];
  const digits = name.replace(/^[A-Z]+/, "");
  const numero = digits === "" ? null : parseInt(digits, 10);

  return tokens.some((token) => {
    const range = parseProductRange(token);

    if (range) {
      if (numero === null || Number.isNaN(numero)) return false;
      if (range.prefix && prefix !== range.prefix) return false;

      return numero >= range.start && numero <= range.end;
    }

    const termo = token.toUpperCase();

    return /^\d+$/.test(termo)
      ? digits.startsWith(termo)
      : name.startsWith(termo);
  });
};
